import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { useBulkActionsState } from 'src/components/common/BulkActions/useBulkActionsState';
import { BulkActionGroup } from 'src/components/Listings/InventoryActionDropdown/components/types';
import { useFilterQueryState } from 'src/components/MainFilterBar/useFilterQueryState';
import { usePurchaseTileBulkActionsState } from 'src/components/Purchases/BulkActions/PurchasesTileBulkActions/usePurchaseTileBulkActionsState';
import { useBulkAdjustCost } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkAdjustCost';
import { useBulkCancelPurchase } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkCancelPurchase';
import { useBulkConvertPurchasePaymentsAction } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkConvertPurchasePaymentsAction';
import { useBulkDeleteTicketGroup } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkDeleteTicketGroup';
import { useBulkEditCommissionAction } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkEditCommissionAction';
import { useBulkEditTagsAction } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkEditTagsAction';
import { useBulkMarkPurchaseAsPaid } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkMarkPurchaseAsPaid';
import { useBulkUpdateDeliveryType } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkUpdateDeliveryType';
import { useBulkUpdateInternalNotesAction } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkUpdateInternalNotesAction';
import { useBulkUpdatePurchaseCurrencyCode } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkUpdatePurchaseCurrencyCodeAction';
import { useBulkUpdatePurchaseSettings } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkUpdatePurchaseGeneralSettings';
import { useBulkUpdateSeatTraits } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkUpdateSeatTraits';
import { useExportPurchasesAction } from 'src/components/Purchases/PurchaseActionDropdown/components/LaunchExportPurchasesAction/useExportPurchasesAction';
import { Content } from 'src/contexts/ContentContext/Content';
import { vars } from 'src/core/themes';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ActionOutboxEntityType,
  PurchaseOrderQuery,
} from 'src/WebApiController';

const usePurchaseActions = (
  isEventView: boolean,
  isLoading: boolean,
  setIsLoading: (_: boolean) => void
): BulkActionGroup | undefined => {
  const { filterQueryWithEventIds } = useFilterQueryState<PurchaseOrderQuery>(
    ActionOutboxEntityType.Purchase,
    undefined,
    isEventView
  );
  const bulkEditTags = useBulkEditTagsAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined
  );
  const purchaseActions = [bulkEditTags.actionItems].flatMap((items) => items);
  if (isEventView || isEmpty(purchaseActions)) {
    return undefined;
  }
  return {
    id: ContentId.Purchase,
    label: <Content id={ContentId.Purchase} />,
    actions: purchaseActions,
  };
};

const useSettingsActions = (
  isEventView: boolean,
  isLoading: boolean,
  setIsLoading: (_: boolean) => void
): BulkActionGroup | undefined => {
  const { areBulkActionsEnabled } = useBulkActionsState();
  const { filterQueryWithEventIds } = usePurchaseTileBulkActionsState(
    undefined,
    isEventView
  );

  const bulkUpdateGeneralSettings = useBulkUpdatePurchaseSettings(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined,
    isEventView
  );

  const bulkUpdateDeliveryType = useBulkUpdateDeliveryType(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined,
    isEventView
  );

  const bulkUpdateSeatTraits = useBulkUpdateSeatTraits(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined,
    isEventView
  );

  const bulkUpdateInternalNotes = useBulkUpdateInternalNotesAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined,
    isEventView
  );

  const bulkEditCommissionAction = useBulkEditCommissionAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined,
    isEventView
  );

  const bulkCancelPurchaseAction = useBulkCancelPurchase(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined,
    isEventView
  );

  const bulkDeleteTicketGroupAction = useBulkDeleteTicketGroup(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined,
    isEventView
  );

  const bulkUpdatePurchaseCurrencyCode = useBulkUpdatePurchaseCurrencyCode(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined,
    isEventView
  );

  const bulkAdjustCost = useBulkAdjustCost(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined,
    isEventView
  );

  const exportPurchasesAction = useExportPurchasesAction({
    style: { color: vars.color.textStrong, width: '100%' },
    variant: 'text',
    shape: 'rect',
    filterQuery: filterQueryWithEventIds,
    disabled: !areBulkActionsEnabled,
    textContentId: ContentId.ExportPurchases,
    textOnly: true,
    isEventView: isEventView ?? false,
  });

  const settingActions = [
    bulkUpdateGeneralSettings.actionItems,
    bulkUpdateDeliveryType.actionItems,
    bulkUpdateSeatTraits.actionItems,
    bulkUpdateInternalNotes.actionItems,
    bulkEditCommissionAction.actionItems,
    bulkCancelPurchaseAction.actionItems,
    bulkDeleteTicketGroupAction.actionItems,
    bulkUpdatePurchaseCurrencyCode.actionItems,
    bulkAdjustCost.actionItems,
    exportPurchasesAction.actionItems,
  ].flatMap((items) => items);
  if (isEmpty(settingActions)) {
    return undefined;
  }
  return {
    id: ContentId.Settings,
    label: <Content id={ContentId.Settings} />,
    actions: settingActions,
  };
};

const useAccountingActions = (
  isEventView: boolean,
  isLoading: boolean,
  setIsLoading: (_: boolean) => void
): BulkActionGroup | undefined => {
  const { filterQueryWithEventIds } = useFilterQueryState<PurchaseOrderQuery>(
    ActionOutboxEntityType.Purchase,
    undefined,
    isEventView
  );

  const bulkConvertPayments = useBulkConvertPurchasePaymentsAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined,
    isEventView
  );

  const bulkMarkPurchaseAsPaidAction = useBulkMarkPurchaseAsPaid(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined
  );

  const accountingActions = [
    bulkConvertPayments.actionItems,
    bulkMarkPurchaseAsPaidAction.actionItems,
  ].flatMap((items) => items);
  if (isEmpty(accountingActions)) {
    return undefined;
  }
  return {
    id: ContentId.Accounting,
    label: <Content id={ContentId.Accounting} />,
    actions: accountingActions,
  };
};

export const usePurchaseBulkActions = (
  isEventView: boolean
): BulkActionGroup[] => {
  const [isLoading, setIsLoading] = useState(false);

  const actionGroups: BulkActionGroup[] = [];

  const purchaseActionGroup = usePurchaseActions(
    isEventView,
    isLoading,
    setIsLoading
  );

  if (purchaseActionGroup) {
    actionGroups.push(purchaseActionGroup);
  }

  const settingsActionGroup = useSettingsActions(
    isEventView,
    isLoading,
    setIsLoading
  );
  if (settingsActionGroup) {
    actionGroups.push(settingsActionGroup);
  }

  const accountingActionGroup = useAccountingActions(
    isEventView,
    isLoading,
    setIsLoading
  );
  if (accountingActionGroup) {
    actionGroups.push(accountingActionGroup);
  }

  return actionGroups;
};
